import { Form, FormControl, FormField } from "@/components/ui/form/form";
import PhoneInputField from "@/components/ui/phone-input/phone-input";
import { removeDialCode } from "@/components/ui/phone-input/phone-input.utils";
import SubmitButton from "@/components/ui/submit-button";
import { useToast } from "@/components/ui/use-toast";
import { PHONE_NUMBER_STATEMENT } from "@/constants/common-constants";
import { COUNTRIES } from "@/constants/countries";
import { useAuth } from "@/context/auth-context";
import { userKeys } from "@/features/auth/keys";
import useUpdateGuestPhone from "@/features/register/hooks/useUpdateGuestPhone";
import { queryClient } from "@/query-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "@tanstack/react-router";
import { CheckCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const phoneFormSchema = z.object({
	phone: z
		.string({ message: "Please enter a phone number" })
		.min(10, "Please enter a phone number")
		.max(15, "Phone number must not exceed 15 digits"),
	countryCode: z
		.number({ message: "Please enter a phone number" })
		.min(1, "Please select a valid country"),
});

type PhoneFormSchema = z.infer<typeof phoneFormSchema>;

function formatPhoneWithDialCode(
	phoneNumber: string,
	phoneGlobalCountryId: number,
): string {
	const country = Object.values(COUNTRIES).find(
		(c) => c.id === String(phoneGlobalCountryId),
	);
	const dialCode = country?.secondary ?? "";
	return dialCode ? `+${dialCode} ${phoneNumber}` : phoneNumber;
}

export type VerifyPhoneChangePhoneSectionProps = {
	sendCodeButton: ReactNode;
};

export function VerifyPhoneChangePhoneSection({
	sendCodeButton,
}: VerifyPhoneChangePhoneSectionProps) {
	const { user } = useAuth();
	const { toast } = useToast();
	const router = useRouter();
	const [isEditingPhone, setIsEditingPhone] = useState(false);
	const phoneInputRef = useRef<HTMLInputElement>(null);

	const updatePhone = useUpdateGuestPhone();

	const phoneForm = useForm<PhoneFormSchema>({
		resolver: zodResolver(phoneFormSchema),
		mode: "onBlur",
		defaultValues: {
			phone: "",
			countryCode: undefined,
		},
	});

	useEffect(() => {
		if (!isEditingPhone) return;
		const country = Object.values(COUNTRIES).find(
			(c) => c.id === String(user.phone_global_country_id),
		);
		const dial = country?.secondary ?? "";
		const digits = `${dial}${user.phone_number}`.replace(/\D/g, "");
		phoneForm.reset({
			phone: digits,
			countryCode: user.phone_global_country_id,
		});
		const focusTimer = setTimeout(() => phoneInputRef.current?.focus(), 100);
		return () => clearTimeout(focusTimer);
	}, [
		isEditingPhone,
		user.phone_number,
		user.phone_global_country_id,
		phoneForm,
	]);

	const handleSavePhone = (values: PhoneFormSchema) => {
		const getCountry = Object.values(COUNTRIES).find(
			(country) => country.id === String(values.countryCode),
		);
		if (!getCountry) {
			phoneForm.setError("countryCode", {
				type: "manual",
				message: "Invalid country selected. Please select a valid country.",
			});
			return;
		}
		const localPhone = getCountry.secondary
			? removeDialCode(values.phone, getCountry.secondary)
			: values.phone;
		const phone = localPhone.replace(/\D/g, "");
		if (!phone) {
			phoneForm.setError("phone", {
				type: "manual",
				message: "Please enter a valid phone number.",
			});
			return;
		}

		updatePhone.mutate(
			{
				guestId: user.id,
				phone,
				phone_global_country_id: values.countryCode,
				global_country_id: values.countryCode,
			},
			{
				onSuccess: async (updatedUser) => {
					queryClient.setQueryData(userKeys.userInformation(), updatedUser);
					await router.invalidate();
					setIsEditingPhone(false);
					toast({
						title: "Phone number updated",
						description:
							"Your number was saved. Tap below when you’re ready to receive the code.",
					});
				},
				onError: (error) => {
					toast({
						title: "Couldn’t update phone number",
						description: error.message,
						variant: "destructive",
					});
				},
			},
		);
	};

	const displayPhone = formatPhoneWithDialCode(
		user.phone_number,
		user.phone_global_country_id,
	);

	if (isEditingPhone) {
		return (
			<Form {...phoneForm}>
				<form
					className="w-full space-y-4 text-left mb-8"
					onSubmit={phoneForm.handleSubmit(handleSavePhone)}
				>
					<FormField
						control={phoneForm.control}
						name="phone"
						render={({ field }) => (
							<FormControl>
								<PhoneInputField
									placeholder="000"
									ref={phoneInputRef}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									onCountryChange={(countryCode) =>
										phoneForm.setValue("countryCode", countryCode)
									}
									onError={(message) =>
										phoneForm.setError("phone", {
											type: "manual",
											message,
										})
									}
								/>
							</FormControl>
						)}
					/>
					<FormField
						control={phoneForm.control}
						name="countryCode"
						render={({ field }) => (
							<FormControl>
								<input
									type="hidden"
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
								/>
							</FormControl>
						)}
					/>
					{phoneForm.formState.errors.phone ? (
						<p className="text-[0.8rem] font-medium text-utility-red">
							{phoneForm.formState.errors.phone.message}
						</p>
					) : phoneForm.formState.errors.countryCode ? (
						<p className="text-[0.8rem] font-medium text-utility-red">
							{phoneForm.formState.errors.countryCode.message}
						</p>
					) : null}
					<p className="text-xs flex items-center gap-2 text-textLight">
						<CheckCircle className="size-4 shrink-0" aria-hidden />
						{PHONE_NUMBER_STATEMENT}
					</p>
					<SubmitButton
						type="submit"
						testId="save-phone-button"
						mutationStatus={updatePhone.status}
						loadingText="Saving..."
						className="w-full text-base font-semibold text-white bg-primary-600 rounded-xl py-3"
					>
						Save phone number
					</SubmitButton>
					<button
						type="button"
						className="w-full text-sm font-semibold text-textDark py-2"
						onClick={() => {
							setIsEditingPhone(false);
							phoneForm.reset();
						}}
					>
						Cancel
					</button>
				</form>
			</Form>
		);
	}

	return (
		<>
			<div className="w-full rounded-xl border border-border bg-muted/30 px-4 py-3 mb-3">
				<p className="text-xs font-medium text-textLight uppercase tracking-wider mb-1">
					Your number
				</p>
				<p className="text-lg font-semibold text-textDark tabular-nums">
					{displayPhone}
				</p>
			</div>
			<button
				type="button"
				className="text-sm font-semibold text-primary-600 mb-8 w-full text-center"
				onClick={() => setIsEditingPhone(true)}
				data-testid="edit-phone-button"
			>
				Wrong number? Update it
			</button>
			{sendCodeButton}
		</>
	);
}
